import { ModeDefinition, Structure } from '@wix/thunderbolt-becky-types'
import { DataForMeshCalculation, MeshComponent, OverridesData } from '../mesh'
import { MeshResult } from '../../../types/css'
import _ from 'lodash'
import { getMeshComponents, getMeshData, getStylesAndWedgesAndRotated } from '../meshUtils'
import { getDefaultCompId, HOVER_SUFFIX, MODES_TYPE } from '@wix/thunderbolt-commons'

const getRotatedComponentsIds = (components: Array<MeshComponent>): Array<string> =>
	components.filter((comp) => comp.rotationInDegrees).map((comp) => comp.id)

// If component is rotated only in one mode, we should add fake rotation in the other mode in order to get the right style
const addFakeRotated = (
	meshComponents: Array<MeshComponent>,
	rotatedComponentsIds: Array<string>
): Array<MeshComponent> =>
	meshComponents.map((comp) =>
		rotatedComponentsIds.includes(comp.id) ? { ...comp, rotationInDegrees: comp.rotationInDegrees || 360 } : comp
	)

const getIsHoverBoxEmptyInMode = (
	components: Array<string>,
	resolvedModeStructure: Structure,
	currentModeId: string
): boolean =>
	!components
		.map((compId) => {
			const compModes = resolvedModeStructure[compId].modes || {}
			const isHiddenByModes = compModes.isHiddenByModes || false
			const modeOverride = (compModes.overrides || []).find(
				({ modeIds }) => modeIds && modeIds[0] === currentModeId
			)

			return (modeOverride || {}).isHiddenByModes === undefined ? isHiddenByModes : modeOverride!.isHiddenByModes
		})
		.includes(false)

export const getMeshResultForHoverBox = (
	hoverBoxCompId: string,
	compMeshData: DataForMeshCalculation,
	overridesData: OverridesData
): MeshResult => {
	const { compStructure: fullCompStructure, compData: data, resolvedModes: resolvedStructure } = compMeshData
	const definitions = _.get(fullCompStructure, 'modes.definitions', []) as Array<ModeDefinition>

	const isHoverComp = hoverBoxCompId.endsWith(HOVER_SUFFIX)
	const mode = isHoverComp ? MODES_TYPE.hover : MODES_TYPE.default

	const currentModeId = definitions.find((def) => def.type === mode)!.modeId
	const resolvedModeStructure = resolvedStructure![currentModeId]

	const hoverBoxComponents = resolvedModeStructure[hoverBoxCompId].components || []
	const isHoverBoxEmpty = getIsHoverBoxEmptyInMode(hoverBoxComponents, resolvedModeStructure, currentModeId)

	const modeComponents = getMeshComponents(hoverBoxCompId, resolvedModeStructure)
	const rotatedComponents = getRotatedComponentsIds(modeComponents)

	const meshData = getMeshData(
		{
			compStructure: resolvedModeStructure[hoverBoxCompId],
			compData: data,
		},
		addFakeRotated(modeComponents, rotatedComponents),
		overridesData,
		isHoverBoxEmpty ? { minLockMargin: 0 } : {}
	)

	const meshResult = getStylesAndWedgesAndRotated(
		hoverBoxCompId,
		{
			...meshData,
			styles: meshData.styles,
		},
		false,
		true
	)

	const allWedges = meshResult.wedges.map((wedgeId) => getDefaultCompId(wedgeId))
	const allRotated = meshResult.rotatedComponents.map((rotatedCompId) => getDefaultCompId(rotatedCompId))

	return {
		...meshResult,
		rotatedComponents: allRotated,
		wedges: allWedges,
	}
}
